/**
 * PMPipelineView - Main Pipeline Tab
 *
 * Default landing page for PMs showing pipeline buckets:
 * 1. Pending Quotes - quotes without orders
 * 2. Orders Awaiting Work Order - orders without work orders
 * 3. Ready for Write-Up - email intakes pending
 * 4. In Production - published work orders
 * 5. Ready for Close-Out - production complete
 */

import { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from "react";
import PMPipelineBucket from "./PMPipelineBucket";

// Bucket configuration with helper text
// IMPORTANT: These IDs must match what the backend returns in transformToPMPipelineBuckets()
const BUCKET_CONFIG = {
  pending_quotes: {
    id: "pending_quotes",
    name: "Pending Quotes",
    description: "Quotes sent, not yet won",
    helperText: "Quote status changes to WON to move forward.",
    color: "blue",
    icon: "📋",
  },
  unscheduled: {
    id: "unscheduled",
    name: "Unscheduled",
    description: "Won but missing ship date",
    helperText: "Add ship date to quote to continue.",
    color: "orange",
    icon: "📅",
  },
  won_needs_wo: {
    id: "won_needs_wo",
    name: "Won - Needs Work Order",
    description: "Won quotes ready for PM assignment",
    helperText: "Assign to yourself and generate a work order.",
    color: "yellow",
    icon: "📦",
  },
  in_production: {
    id: "in_production",
    name: "In Production",
    description: "Active orders with work in progress",
    helperText: "Monitor progress. Mark PDFs ready when proofs are sent.",
    color: "green",
    icon: "🏭",
  },
  ready_closeout: {
    id: "ready_closeout",
    name: "Ready for Close-Out",
    description: "Production complete, needs final review",
    helperText: "Verify completion and finalize project documentation.",
    color: "purple",
    icon: "✅",
  },
};

const PMPipelineView = forwardRef(function PMPipelineView({
  acquireAccessToken,
  onSelectProject,
  onGenerateWorkOrder,
  onViewWorkOrder,
  onAssignToMe,
  onMarkPDFReady,
}, ref) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [expandedBuckets, setExpandedBuckets] = useState({
    pending_quotes: false, // Large bucket, collapsed by default
    unscheduled: true,
    won_needs_wo: true, // Primary action bucket
    in_production: true,
    ready_closeout: false, // Collapsed by default
  });
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load pipeline snapshot
  const loadPipelineSnapshot = useCallback(async () => {
    try {
      setLoading(true);
      const accessToken = await acquireAccessToken();
      if (!accessToken) {
        setError("Authentication required");
        return;
      }

      const res = await fetch("/api/pm-center/pipeline/snapshot", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!res.ok) {
        throw new Error(`Failed to load pipeline: ${res.status}`);
      }

      const data = await res.json();
      setSnapshot(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error("Error loading pipeline snapshot:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [acquireAccessToken]);

  // Expose refresh method to parent via ref
  useImperativeHandle(ref, () => ({
    refresh: loadPipelineSnapshot,
  }), [loadPipelineSnapshot]);

  // Initial load
  useEffect(() => {
    loadPipelineSnapshot();
  }, [loadPipelineSnapshot]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(loadPipelineSnapshot, 60000);
    return () => clearInterval(interval);
  }, [loadPipelineSnapshot]);

  // Toggle bucket expansion
  const toggleBucket = (bucketId) => {
    setExpandedBuckets((prev) => ({
      ...prev,
      [bucketId]: !prev[bucketId],
    }));
  };

  // Calculate totals
  const totals = useMemo(() => {
    if (!snapshot?.buckets) {
      return { pending: 0, active: 0, closeout: 0 };
    }

    const bucketCounts = {};
    snapshot.buckets.forEach((b) => {
      bucketCounts[b.id] = b.count;
    });

    return {
      pending:
        (bucketCounts.pending_quotes || 0) +
        (bucketCounts.awaiting_wo || 0) +
        (bucketCounts.ready_writeup || 0),
      active: bucketCounts.in_production || 0,
      closeout: bucketCounts.ready_closeout || 0,
    };
  }, [snapshot]);

  // Format last updated time
  const formatLastUpdated = (date) => {
    if (!date) return "";
    const now = new Date();
    const diffSeconds = Math.floor((now - date) / 1000);

    if (diffSeconds < 60) return "just now";
    if (diffSeconds < 3600)
      return `${Math.floor(diffSeconds / 60)} min${diffSeconds >= 120 ? "s" : ""} ago`;
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (loading && !snapshot) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading pipeline...</p>
        </div>
      </div>
    );
  }

  if (error && !snapshot) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <div className="text-red-500 text-4xl mb-4">!</div>
          <p className="text-red-600 font-medium mb-2">
            Failed to load pipeline
          </p>
          <p className="text-gray-500 text-sm mb-4">{error}</p>
          <button
            onClick={loadPipelineSnapshot}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Pipeline Header with Stats */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <h2 className="text-lg font-semibold text-gray-900">Pipeline</h2>

            {/* Summary Stats */}
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                <span className="text-gray-600">
                  {totals.pending} pending
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span className="text-gray-600">{totals.active} active</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                <span className="text-gray-600">
                  {totals.closeout} close-out
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-xs text-gray-400">
                Updated {formatLastUpdated(lastUpdated)}
              </span>
            )}
            <button
              onClick={loadPipelineSnapshot}
              disabled={loading}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh pipeline"
            >
              <svg
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Pipeline Buckets */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {snapshot?.buckets?.map((bucket) => {
          const config = BUCKET_CONFIG[bucket.id];
          if (!config) return null;

          return (
            <PMPipelineBucket
              key={bucket.id}
              bucket={{
                ...config,
                count: bucket.count,
                cards: bucket.cards || [],
              }}
              expanded={expandedBuckets[bucket.id]}
              onToggle={() => toggleBucket(bucket.id)}
              onSelectProject={onSelectProject}
              onGenerateWorkOrder={onGenerateWorkOrder}
              onViewWorkOrder={onViewWorkOrder}
              onAssignToMe={onAssignToMe}
              onMarkPDFReady={onMarkPDFReady}
            />
          );
        })}

        {(!snapshot?.buckets || snapshot.buckets.length === 0) && (
          <div className="text-center py-12 text-gray-500">
            <p>No pipeline data available</p>
          </div>
        )}
      </div>
    </div>
  );
});

export default PMPipelineView;
